/**
 * dashboardSiteScope — descendant-aware visibility predicate for the Dashboard
 * Portfolio Overview.
 *
 * BUG (aurora-qa-2): the dashboard filtered portfolio sites with an EXACT
 * `site.companyId === customerId` match. That dropped two whole classes of
 * already-authorized, already-loaded sites:
 *
 *   1. SUB-COMPANY sites — sites owned by a DESCENDANT org of the user's
 *      company. `fetchSites` loads these (gated by Firestore rules) and stamps
 *      them `_sourceScope: 'subCompany'`, but their `companyId` is the
 *      descendant id, not the user's, so the exact match excluded them.
 *   2. ADMIN all-access — a Deep-Atomic admin whose own sites live under
 *      various sub-companies saw 0, because none of those `companyId`s equal
 *      the admin's `customerId`.
 *
 * This helper resolves the SAME visible set the working site list uses:
 *   - Admins see every loaded site (no company filter).
 *   - Otherwise a site is in scope when its `companyId` is the user's company
 *     OR any DESCENDANT of it (resolved via `collectDescendantCompanyIds` over
 *     the verified subtree — identical to SiteRagEngine's filter), OR the site
 *     is companyless (personal / not-yet-shared → owned by the user), OR the
 *     site was loaded as a company/sub-company peer (`_sourceScope`, a
 *     belt-and-braces signal that `fetchSites` already authorized it as part of
 *     the subtree even if the subtree company records are sparse).
 *
 * Pure + dependency-light so it can be unit-tested and reused by every
 * portfolio surface. It only ever SUBSETS the already-fetched, already-
 * authorized `siteRag.sites` — it can never widen access.
 */
import type { Company } from '../../types/company';
import type { SiteEntity } from '../../types/site';
import { collectDescendantCompanyIds } from '../companyHierarchy';

export interface DashboardScopeOptions {
  /** Deep-Atomic admin → all-access (no company filter). */
  isAdmin: boolean;
  /**
   * The verified company subtree (own org + descendants) as full records,
   * from the store's `subtreeCompanies` (populated by `fetchSubtreeCompanies`).
   * Used to resolve the descendant company-id set. May be empty for a
   * solo/leaf user, in which case scope falls back to the own id + the
   * `_sourceScope` signal.
   */
  subtreeCompanies: readonly Company[];
}

/**
 * Resolve the set of company ids visible to a non-admin user: the user's own
 * company (`customerId`) ∪ all descendant company ids. Mirrors
 * SiteRagEngine's `effectiveCompanyFilterIds`. Always includes `customerId`
 * itself (defensive — `collectDescendantCompanyIds` does too).
 */
export function resolveVisibleCompanyIds(
  customerId: string | null | undefined,
  subtreeCompanies: readonly Company[],
): Set<string> {
  if (typeof customerId !== 'string' || customerId.trim().length === 0) {
    return new Set<string>();
  }
  // collectDescendantCompanyIds always folds in `customerId` itself plus any
  // descendant reachable by parentCompanyId edges OR a `path[]` containing it.
  const ids = collectDescendantCompanyIds([...subtreeCompanies], customerId);
  ids.add(customerId);
  return ids;
}

/**
 * Is this site in the dashboard's visible portfolio scope?
 *
 * @param site        A loaded site (already authorized by `fetchSites`).
 * @param customerId  The signed-in user's company id (may be null).
 * @param opts        Admin flag + verified subtree companies.
 */
export function isSiteInDashboardScope(
  site: Pick<SiteEntity, 'companyId' | '_sourceScope'>,
  customerId: string | null | undefined,
  opts: DashboardScopeOptions,
): boolean {
  // Admins see every loaded site.
  if (opts.isAdmin) return true;

  const siteCompanyId =
    typeof site.companyId === 'string' && site.companyId.trim().length > 0
      ? site.companyId
      : null;

  // Companyless site → personal / not-yet-shared → owned by the signed-in
  // user. Always in scope (matches the pre-fix `!site.companyId` arm).
  if (!siteCompanyId) return true;

  // Descendant-aware company match.
  const visible = resolveVisibleCompanyIds(customerId, opts.subtreeCompanies);
  if (visible.has(siteCompanyId)) return true;

  // Belt-and-braces: `fetchSites` already authorized + loaded this site as a
  // company/sub-company peer (the working site list shows it). Trust that
  // signal even when the subtree company records are sparse / not yet loaded,
  // so the Portfolio Overview can never under-count a site the user can see.
  if (site._sourceScope === 'company' || site._sourceScope === 'subCompany') {
    return true;
  }

  // No customerId at all → can't scope by company; fall back to including the
  // site (the pre-fix `!customerId` arm) so we never hide an authorized site.
  if (typeof customerId !== 'string' || customerId.trim().length === 0) {
    return true;
  }

  return false;
}

/** Curried predicate for `Array.prototype.filter`. */
export function dashboardScopePredicate(
  customerId: string | null | undefined,
  opts: DashboardScopeOptions,
): (site: Pick<SiteEntity, 'companyId' | '_sourceScope'>) => boolean {
  return (site) => isSiteInDashboardScope(site, customerId, opts);
}
